import { useEffect, useState, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RefreshCw, Wrench } from 'lucide-react';
import { Button } from '@/components/ui';
import { isApiError } from '@/lib/api';
import { formatDateTime } from '@/lib/format';
import { isImpersonating, isPlatformOperator, maintenanceOf, useSession } from '@/lib/session';

/** Full-screen maintenance notice. The message is plain text from the server (rendered as text, never HTML). */
export function MaintenanceScreen({ message, endsAt, onRetry, onSignOut }: { message: string; endsAt: string | null; onRetry?: () => void; onSignOut?: () => void }) {
  return (
    <div className="boot" role="main">
      <div style={{ maxWidth: '32rem', margin: '0 auto', padding: 'var(--space-6, 32px) var(--space-4, 16px)', textAlign: 'center' }}>
        <Wrench size={36} aria-hidden style={{ margin: '0 auto var(--space-3, 12px)' }} />
        <h1 style={{ margin: '0 0 var(--space-2, 8px)' }}>Scheduled maintenance</h1>
        <p style={{ whiteSpace: 'pre-wrap', margin: '0 0 var(--space-3, 12px)' }}>{message || 'We are performing scheduled maintenance. Please try again shortly.'}</p>
        {endsAt && <p className="subtle">Expected to finish by {formatDateTime(endsAt)}.</p>}
        <div className="row" style={{ justifyContent: 'center', ['--row-gap' as string]: 'var(--space-2)', marginTop: 'var(--space-4, 16px)' }}>
          {onRetry && (
            <Button variant="primary" icon={<RefreshCw size={15} />} onClick={onRetry}>
              Try again
            </Button>
          )}
          {onSignOut && (
            <Button variant="ghost" onClick={onSignOut}>
              Sign out
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Shows MaintenanceScreen instead of `children` for non-operators while maintenance is active —
 * either announced by the session payload or reported by any API call (503 MAINTENANCE_MODE).
 * Operators and impersonation sessions pass (the server lets them through as well). The server
 * enforces maintenance on its own; this only avoids showing broken screens.
 */
export function MaintenanceGate({ children }: { children: ReactNode }) {
  const session = useSession();
  const qc = useQueryClient();
  const [fromApi, setFromApi] = useState<{ message: string; endsAt: string | null } | null>(null);

  useEffect(() => {
    const capture = (err: unknown) => {
      if (!isApiError(err) || err.code !== 'MAINTENANCE_MODE') return;
      const d = (err.details ?? {}) as { message?: string; endsAt?: string | null };
      setFromApi({ message: d.message ?? err.message, endsAt: d.endsAt ?? null });
    };
    const offQueries = qc.getQueryCache().subscribe((e) => {
      if (e.type === 'updated' && e.action.type === 'error') capture(e.action.error);
    });
    const offMutations = qc.getMutationCache().subscribe((e) => {
      if (e.type === 'updated' && e.action.type === 'error') capture(e.action.error);
    });
    return () => {
      offQueries();
      offMutations();
    };
  }, [qc]);

  const exempt = isPlatformOperator(session.user) || isImpersonating(session);
  const m = maintenanceOf(session);
  if (!exempt && (m?.active || fromApi)) {
    return (
      <MaintenanceScreen
        message={fromApi?.message ?? m?.message ?? ''}
        endsAt={fromApi?.endsAt ?? m?.endsAt ?? null}
        onRetry={() => {
          setFromApi(null);
          void session.refresh();
          void qc.invalidateQueries({ refetchType: 'active' });
        }}
        onSignOut={session.user ? () => void session.signOut() : undefined}
      />
    );
  }
  return <>{children}</>;
}
